import { useEffect, useState } from "react";
import {
  Stack, Title, Text, Textarea, TextInput, NumberInput, Switch, Button, Select,
  Paper, Alert, Group, Badge, Divider, Loader,
} from "@mantine/core";
import { IconAlertTriangle, IconShieldCheck, IconSend } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { getConsentSettings, updateConsentSettings, verifyConsentChain, sendConsentTest } from "../api";

// Language endonyms — shown natively regardless of UI language, so not translated.
const LOCALES = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
];

const emptyTemplate = () => ({
  id: "", name: "Standard", locale: "de", subject: "",
  body: "", defaultValidityPeriod: "months12",
});

export default function ConsentSettingsPage() {
  const { t } = useTranslation("Veft");
  const [s, setS] = useState(null);
  const [tpl, setTpl] = useState(emptyTemplate());
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [chain, setChain] = useState(null);

  const VALIDITY = [
    { value: "months6", label: t("consent_validity_months6") },
    { value: "months12", label: t("consent_validity_months12") },
    { value: "months24", label: t("consent_validity_months24") },
    { value: "indefinite", label: t("consent_validity_indefinite") },
  ];

  useEffect(() => {
    getConsentSettings().then((data) => {
      setS(data);
      setTpl(data.templates?.[0] ?? emptyTemplate());
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateConsentSettings({
        templates: [tpl],
        activated: s.activated,
        // Mantine NumberInput emits "" while empty; coerce so int? binding never 400s.
        defaultLinkExpiryDays: Number(s.defaultLinkExpiryDays) || 30,
        overdueThresholdDays: Number(s.overdueThresholdDays) || 14,
        reminderAfterDays: Number(s.reminderAfterDays) || 0,
        minimizeOnErasure: s.minimizeOnErasure,
        controllerIdentity: s.controllerIdentity ?? "",
        micrositeFooter: s.micrositeFooter ?? "",
        micrositeAccentColor: s.micrositeAccentColor ?? "",
      });
      setS(updated);
      setTpl(updated.templates?.[0] ?? emptyTemplate());
      notifications.show({ color: "teal", message: t("consent_settings_saved") });
    } catch (err) {
      // Backend rejects activation while the template still holds the example text — surface why.
      notifications.show({ color: "red", message: err?.message || t("consent_settings_save_error") });
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => setChain(await verifyConsentChain());

  // Self-test: email myself a real consent email linking to an isolated preview microsite.
  const tryFlow = async () => {
    setTesting(true);
    try {
      const res = await sendConsentTest();
      notifications.show({ color: "teal", message: t("consent_test_send_sent", { email: res?.sentTo ?? "" }) });
    } catch (err) {
      notifications.show({ color: "red", message: err?.message || t("consent_test_send_error") });
    } finally {
      setTesting(false);
    }
  };

  if (!s) return <Group justify="center" p="xl"><Loader /></Group>;

  const set = (patch) => setS({ ...s, ...patch });
  const setT = (patch) => setTpl({ ...tpl, ...patch });
  // Can't activate while the body still holds the shipped example marker (matches the backend guard).
  const bodyHasPlaceholder = (tpl.body || "").includes("BEISPIELTEXT");

  return (
    <Stack gap="lg" maw={760}>
      <Title order={3}>{t("consent_settings_title")}</Title>

      <Alert color="yellow" icon={<IconAlertTriangle size={18} />}>
        {t("consent_settings_disclaimer")}
      </Alert>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text fw={600}>{t("consent_settings_request_template")}</Text>
          <Group grow>
            <TextInput label={t("consent_settings_tpl_name")} value={tpl.name} onChange={(e) => setT({ name: e.currentTarget.value })} />
            <Select label={t("consent_settings_tpl_language")} data={LOCALES} value={tpl.locale} onChange={(v) => setT({ locale: v })} allowDeselect={false} />
            <Select label={t("consent_settings_tpl_validity")} data={VALIDITY} value={tpl.defaultValidityPeriod}
              onChange={(v) => setT({ defaultValidityPeriod: v })} allowDeselect={false} />
          </Group>
          <TextInput label={t("consent_settings_tpl_subject")} value={tpl.subject} onChange={(e) => setT({ subject: e.currentTarget.value })} />
          <Textarea label={t("consent_settings_tpl_body")} autosize minRows={6} value={tpl.body}
            onChange={(e) => setT({ body: e.currentTarget.value })}
            description={`${t("consent_settings_placeholders_label")} {{candidateName}} {{tenantName}} {{controllerName}} {{retentionPeriod}} {{withdrawUrl}}`} />
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>{t("consent_activation_title")}</Text>
            <Badge color={s.activated ? "teal" : "gray"} variant="light">
              {s.activated ? t("consent_activation_status_on") : t("consent_activation_status_off")}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">{t("consent_activation_desc")}</Text>
          {bodyHasPlaceholder && (
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
              {t("consent_activation_placeholder_warning")}
            </Alert>
          )}
          <Switch
            label={t("consent_activation_switch")}
            checked={!!s.activated}
            disabled={!s.activated && bodyHasPlaceholder}
            onChange={(e) => set({ activated: e.currentTarget.checked })}
          />
          <Group>
            <Button variant="light" size="sm" leftSection={<IconSend size={16} />} loading={testing}
              disabled={!s.activated} onClick={tryFlow}>
              {t("consent_test_send_button")}
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text fw={600}>{t("consent_settings_controller_microsite")}</Text>
          <Textarea label={t("consent_settings_controller_identity")} autosize minRows={2}
            value={s.controllerIdentity ?? ""} onChange={(e) => set({ controllerIdentity: e.currentTarget.value })}
            description={t("consent_settings_controller_identity_desc")} />
          <Group grow>
            <TextInput label={t("consent_settings_microsite_footer")} value={s.micrositeFooter ?? ""}
              onChange={(e) => set({ micrositeFooter: e.currentTarget.value })} />
            <TextInput label={t("consent_settings_accent_color")} placeholder="#2C5F4A" value={s.micrositeAccentColor ?? ""}
              onChange={(e) => set({ micrositeAccentColor: e.currentTarget.value })} />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text fw={600}>{t("consent_settings_timing_retention")}</Text>
          <Group grow>
            <NumberInput label={t("consent_settings_link_validity")} min={1} max={365}
              value={s.defaultLinkExpiryDays} onChange={(v) => set({ defaultLinkExpiryDays: v })} />
            <NumberInput label={t("consent_settings_overdue_after")} min={1} max={365}
              value={s.overdueThresholdDays} onChange={(v) => set({ overdueThresholdDays: v })} />
            <NumberInput label={t("consent_settings_remind_after")} min={0} max={365}
              value={s.reminderAfterDays ?? 0} onChange={(v) => set({ reminderAfterDays: v })} />
          </Group>
          <Switch
            label={t("consent_settings_minimize_label")}
            description={t("consent_settings_minimize_desc")}
            checked={!!s.minimizeOnErasure}
            onChange={(e) => set({ minimizeOnErasure: e.currentTarget.checked })}
          />
        </Stack>
      </Paper>

      <Group>
        <Button onClick={save} loading={saving}>{t("consent_settings_save")}</Button>
        <Button variant="default" leftSection={<IconShieldCheck size={16} />} onClick={verify}>
          {t("consent_settings_verify_chain")}
        </Button>
        {chain && (
          <Badge color={chain.intact ? "teal" : "red"}>
            {chain.intact
              ? t("consent_settings_chain_intact")
              : t("consent_settings_chain_broken", { seq: chain.breakAtSequence })}
          </Badge>
        )}
      </Group>
    </Stack>
  );
}
